import React, {ReactNode, ReactElement, useRef, KeyboardEvent} from 'react'
import {
    AccordionItem as ChackraAccordionItem,
    AccordionButton,
    AccordionPanel,
    useMultiStyleConfig,
    AccordionButtonProps,
    VisuallyHidden,
    Box,
    Text,
} from '@chakra-ui/react'
import {Checkbox, Icon, Radio} from '_uiComponents'
import {useIntl} from 'react-intl'
import {RefinementType} from '_types'

interface ListingFiltersItemProps {
    title: string | ReactNode
    selectedFilters?: Array<string>
    filter?: RefinementType
    filterType?: 'checkbox' | 'radio'
    ariaLabel?: string
    expandable?: boolean
    previewSelectedValue?: boolean
    children?: ReactNode
    expandIcon?: ReactElement
    collapseIcon?: ReactElement
    screenReaderCategoryLabelToExpand?: string
    screenReaderCategoryLabelExpanded?: string
    previewSelectedItem?: boolean
    handleFilterToggle?: (filter: {value: string}, attributeId: string, selected: boolean, allowMultiple: boolean, action?: string) => void
    handleSorting?: (sorting: string) => void
}

/**
 * @name ListingFiltersItem
 *
 * @component
 *
 * @description
 * Renders an accordion item using Chakra UI's AccordionItem.
 *
 * @param {Object} props - The properties object.
 * @param {string | ReactNode} props.title - The title of the accordion button.
 * @param {ReactNode} props.children - Content showed in the accordion panel.
 * @param {ReactElement | undefined} props.expandIcon - The icon to be displayed when the item is expandable.
 * @param {ReactElement | undefined} props.collapseIcon - The icon to be displayed when the item is collapseable.
 * @param {string} [props.screenReaderCategoryLabelToExpand] - Label to show on screen readers for `toExpand` behaviour.
 * @param {string} [props.screenReaderCategoryLabelExpanded] - Label to show on screen readers for `expanded` behaviour.
 * @param {boolean} [props.previewSelectedItem] - Whether or not to show the selected item
 *
 * @returns {JSX.Element} The rendered `ListingFiltersItem` component.
 */
const ListingFiltersItem = ({
    title,
    selectedFilters,
    filter,
    filterType = 'checkbox',
    ariaLabel,
    expandable = true,
    previewSelectedValue = true,
    children,
    expandIcon,
    collapseIcon,
    screenReaderCategoryLabelToExpand,
    screenReaderCategoryLabelExpanded,
    handleFilterToggle,
    handleSorting,
}: ListingFiltersItemProps): JSX.Element => {
    const styles = useMultiStyleConfig('FiltersList') as Record<string, AccordionButtonProps>
    const {formatMessage} = useIntl()
    const selectValuePreview = useRef<string>('')

    const renderCustomIcon = expandIcon && collapseIcon
    const hasInlineChildren = !expandable && children

    /**
     * Title must be aria hidden only when screen reader labels are passed via props.
     */
    const isTitleAriaHidden = !!screenReaderCategoryLabelToExpand && !!screenReaderCategoryLabelExpanded

    if (filter?.values) {
        const selectableValues = filter.values.map((v) => v.value)
        const selectedValuesNo = selectedFilters?.filter((v) => selectableValues?.includes(v))?.length

        if (selectedValuesNo === 0) {
            selectValuePreview.current = ''
        } else if (selectedValuesNo === 1) {
            const selected = filter.values.find((f) => f.value === selectedFilters?.[0])
            selectValuePreview.current = selected?.description || selected?.label || ''
        } else if (selectedValuesNo) {
            selectValuePreview.current = formatMessage({id: 'label.plp.filters.preview', defaultMessage: 'N filters'}, {0: selectedValuesNo})
        }
    }

    /**
     * Focus on first available filter in the list when opening the accordion item.
     * @param filter
     * @param {boolean} isExpanded
     */
    const onAccordionClick = (isExpanded: boolean) => {
        if (!isExpanded && filter?.values?.[0]?.value) {
            setTimeout(() => {
                const inputElement = document.querySelector(`input[value="${filter.values[0].value}"]`) as HTMLElement
                inputElement?.parentElement?.focus()
            }, 50) // Waits for accordion expanding otherwise the element won't be available
        }
    }

    return (
        <fieldset>
            <VisuallyHidden as="legend">{ariaLabel}</VisuallyHidden>
            <ChackraAccordionItem sx={styles.filterItem} data-testid="accordion-item">
                {({isExpanded}) => (
                    <>
                        <AccordionButton
                            {...(hasInlineChildren ? {as: Box, sx: styles.inlineFilter} : {})}
                            {...styles.accordionButton}
                            data-testid="accordion-button"
                            onClick={() => onAccordionClick(isExpanded)}
                        >
                            <Box aria-hidden={isTitleAriaHidden}>{title}</Box>

                            {isTitleAriaHidden && (
                                <VisuallyHidden>{!isExpanded ? screenReaderCategoryLabelToExpand : screenReaderCategoryLabelExpanded}</VisuallyHidden>
                            )}

                            {hasInlineChildren ? (
                                <Box as="section" {...styles.inlineFilterChildren}>
                                    {children}
                                </Box>
                            ) : (
                                <Text>
                                    {previewSelectedValue && (
                                        <Text as={'span'} sx={styles.filterValuePreview}>
                                            {selectValuePreview.current.toString()}
                                        </Text>
                                    )}
                                    {expandable && (
                                        <>
                                            {renderCustomIcon ? (
                                                <>{isExpanded ? collapseIcon : expandIcon}</>
                                            ) : (
                                                <Icon name="IconChevronDown" size="sm" aria-hidden />
                                            )}
                                        </>
                                    )}
                                </Text>
                            )}
                        </AccordionButton>

                        {!hasInlineChildren ? (
                            <AccordionPanel>
                                {filterType === 'checkbox' && (
                                    <>
                                        {filter?.values?.map((filterValue) => {
                                            const {attributeId} = filter
                                            const {hitCount, label, description} = filterValue
                                            const value = filterValue.label.replace('&', '%26')
                                            const hasHitCount = hitCount ?? false

                                            const isChecked = selectedFilters?.includes(value) || false

                                            return (
                                                <Checkbox
                                                    key={value}
                                                    value={value}
                                                    isChecked={isChecked}
                                                    onChange={() => {
                                                        handleFilterToggle?.({value}, filter.attributeId, isChecked, true, filter.label?.toLowerCase())
                                                    }}
                                                    onKeyDown={(e: KeyboardEvent) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            handleFilterToggle?.({value}, filter.attributeId, isChecked, true, filter.label?.toLowerCase())
                                                        }
                                                    }}
                                                    size="xs"
                                                >
                                                    {attributeId === 'c_filter-size' ? description : label} {hasHitCount ? `( ${hitCount} )` : ''}
                                                </Checkbox>
                                            )
                                        })}
                                    </>
                                )}

                                {filterType === 'radio' && (
                                    <Radio
                                        size="xs"
                                        sx={styles.filtersRadio}
                                        onChange={(value: string) => handleSorting?.(value)}
                                        display={'grid'}
                                        options={filter?.values}
                                        defaultValue={selectedFilters?.[0] || 'best-matches'}
                                    ></Radio>
                                )}
                            </AccordionPanel>
                        ) : null}
                    </>
                )}
            </ChackraAccordionItem>
        </fieldset>
    )
}

export {ListingFiltersItem}
